import React from "react";

/**
 * The decorative artwork in the festival page hero, one drawing per `theme.motif`.
 *
 * <p>Drawn in code, not stored as images, for one reason: a new festival has to be a single
 * database row. An image would mean an upload and a URL in the theme; an SVG here means the new
 * row names an existing motif ("diyas") and the page already knows how to draw it. A row naming a
 * motif nobody drew yet still renders — it falls back to the neutral lamp — so a festival added by
 * someone who never opens this repo is never a broken page.
 *
 * <p>Both colours come from the festival's own palette, so the same drawing reads as Diwali gold
 * or Sankranti orange without a second copy of it.
 */

const Diyas = ({ a, b }) => (
  <>
    <circle cx="200" cy="120" r="80" fill={a} opacity="0.14" />
    <circle cx="200" cy="120" r="46" fill={a} opacity="0.18" />
    <ellipse cx="200" cy="128" rx="16" ry="38" fill={a} />
    <ellipse cx="200" cy="140" rx="7" ry="18" fill="#FFF1C9" />
    <path d="M110 170 Q200 262 290 170 Z" fill={b} />
    <path d="M110 170 H290" stroke={a} strokeWidth="3" />
    <circle cx="70" cy="200" r="34" fill={a} opacity="0.14" />
    <ellipse cx="70" cy="204" rx="8" ry="19" fill={a} />
    <path d="M30 226 Q70 270 110 226 Z" fill={b} />
    <circle cx="330" cy="200" r="34" fill={a} opacity="0.14" />
    <ellipse cx="330" cy="204" rx="8" ry="19" fill={a} />
    <path d="M290 226 Q330 270 370 226 Z" fill={b} />
  </>
);

const Bow = ({ a, b }) => (
  <>
    <path
      d="M150 30 Q330 150 150 270"
      fill="none"
      stroke={a}
      strokeWidth="10"
      strokeLinecap="round"
    />
    <path d="M150 30 L150 270" stroke="#F4E6D6" strokeWidth="2" />
    <path d="M50 150 H340" stroke={a} strokeWidth="5" strokeLinecap="round" />
    <path d="M360 150 l-28 -14 v28 Z" fill={a} />
    <path
      d="M50 150 l-18 -14 M50 150 l-18 14 M66 150 l-18 -14 M66 150 l-18 14"
      stroke={a}
      strokeWidth="3"
      strokeLinecap="round"
    />
    <circle cx="320" cy="60" r="18" fill={b} />
    <circle cx="352" cy="92" r="13" fill={a} />
    <circle cx="310" cy="244" r="18" fill={b} />
    <circle cx="346" cy="214" r="13" fill={a} />
    <circle cx="90" cy="60" r="12" fill={b} />
    <circle cx="80" cy="240" r="12" fill={b} />
  </>
);

const Moon = ({ a, b }) => (
  <>
    <circle cx="262" cy="104" r="96" fill={a} opacity="0.12" />
    <circle cx="262" cy="104" r="66" fill={a} />
    <circle cx="240" cy="90" r="9" fill="#000000" opacity="0.06" />
    <circle cx="282" cy="124" r="13" fill="#000000" opacity="0.06" />
    <circle cx="160" cy="176" r="86" fill="none" stroke={b} strokeWidth="8" />
    <circle
      cx="160"
      cy="176"
      r="66"
      fill="none"
      stroke={b}
      strokeWidth="2"
      strokeDasharray="3 7"
    />
    <circle
      cx="160"
      cy="176"
      r="46"
      fill="none"
      stroke={b}
      strokeWidth="2"
      strokeDasharray="3 7"
    />
    <circle
      cx="160"
      cy="176"
      r="26"
      fill="none"
      stroke={b}
      strokeWidth="2"
      strokeDasharray="3 7"
    />
    <path
      d="M222 238 L270 286"
      stroke={b}
      strokeWidth="12"
      strokeLinecap="round"
    />
    <circle cx="60" cy="40" r="3" fill={a} />
    <circle cx="110" cy="70" r="2" fill={a} />
    <circle cx="370" cy="230" r="3" fill={a} />
    <circle cx="30" cy="140" r="2" fill={a} />
  </>
);

const Thali = ({ a, b }) => (
  <>
    <ellipse cx="200" cy="210" rx="170" ry="54" fill={a} />
    <ellipse cx="200" cy="200" rx="148" ry="40" fill={b} />
    <circle cx="200" cy="120" r="50" fill={a} opacity="0.16" />
    <ellipse cx="200" cy="130" rx="10" ry="26" fill={a} />
    <path d="M150 172 Q200 214 250 172 Z" fill="#C2562B" />
    <ellipse cx="300" cy="196" rx="26" ry="10" fill="#C8102E" />
    <ellipse cx="104" cy="196" rx="26" ry="10" fill="#F4E6D6" />
    <circle cx="88" cy="96" r="14" fill={a} />
    <circle cx="324" cy="92" r="12" fill={a} />
    <circle cx="300" cy="64" r="8" fill="#C8102E" />
  </>
);

const Stars = ({ a }) => (
  <>
    <path
      d="M200 20 L212 54 L248 54 L219 75 L230 110 L200 89 L170 110 L181 75 L152 54 L188 54 Z"
      fill={a}
    />
    <path
      d="M200 100 L130 180 H162 L104 250 H296 L238 180 H270 Z"
      fill="#2F6B45"
    />
    <path d="M188 250 h24 v30 h-24 Z" fill="#7A4A2A" />
    <circle cx="176" cy="170" r="8" fill="#C8102E" />
    <circle cx="226" cy="196" r="8" fill={a} />
    <circle cx="150" cy="230" r="8" fill={a} />
    <circle cx="252" cy="236" r="8" fill="#C8102E" />
    <circle cx="60" cy="60" r="3" fill="#FFFFFF" />
    <circle cx="340" cy="50" r="4" fill="#FFFFFF" />
    <circle cx="360" cy="160" r="3" fill="#FFFFFF" />
    <circle cx="40" cy="180" r="4" fill="#FFFFFF" />
  </>
);

const Kites = ({ a, b }) => (
  <>
    <circle cx="330" cy="70" r="44" fill={a} opacity="0.9" />
    <path d="M170 30 L250 120 L170 210 L90 120 Z" fill={b} />
    <path
      d="M170 30 V210 M90 120 H250"
      stroke="#FFFFFF"
      strokeWidth="2"
      opacity="0.7"
    />
    <path
      d="M170 210 Q150 250 190 270 Q230 290 210 300"
      fill="none"
      stroke="#FFFFFF"
      strokeWidth="2"
    />
    <path d="M300 150 L340 196 L300 242 L260 196 Z" fill="#C8102E" />
    <path
      d="M300 150 V242 M260 196 H340"
      stroke="#FFFFFF"
      strokeWidth="1.5"
      opacity="0.7"
    />
    <path
      d="M300 242 Q290 270 312 292"
      fill="none"
      stroke="#FFFFFF"
      strokeWidth="1.5"
    />
  </>
);

const Rain = ({ a }) => (
  <>
    <path d="M110 280 Q40 140 200 30 Q250 180 110 280 Z" fill="#3E7D4A" />
    <path
      d="M110 280 Q150 150 200 30"
      fill="none"
      stroke="#A9D08E"
      strokeWidth="2"
    />
    <path d="M220 290 Q200 180 330 120 Q340 230 220 290 Z" fill="#5A9A5E" />
    <path
      d="M300 30 q14 24 0 36 q-14 -12 0 -36 Z M350 90 q12 20 0 30 q-12 -10 0 -30 Z M260 70 q10 18 0 27 q-10 -9 0 -27 Z M60 60 q12 20 0 30 q-12 -10 0 -30 Z M370 200 q10 18 0 27 q-10 -9 0 -27 Z"
      fill={a}
    />
  </>
);

const Rakhi = ({ a, b }) => (
  <>
    <path
      d="M10 160 Q200 120 390 160"
      fill="none"
      stroke="#C8102E"
      strokeWidth="6"
    />
    <path d="M10 166 Q200 126 390 166" fill="none" stroke={a} strokeWidth="2" />
    <ellipse cx="200" cy="80" rx="18" ry="40" fill={b} />
    <ellipse cx="200" cy="200" rx="18" ry="40" fill={b} />
    <ellipse cx="140" cy="140" rx="40" ry="18" fill={b} />
    <ellipse cx="260" cy="140" rx="40" ry="18" fill={b} />
    <circle cx="200" cy="140" r="50" fill={a} />
    <circle cx="200" cy="140" r="34" fill="#C8102E" />
    <circle cx="200" cy="140" r="16" fill={a} />
    <path
      d="M24 162 l-8 40 M30 160 l0 42 M376 162 l8 40 M370 160 l0 42"
      stroke={a}
      strokeWidth="3"
      strokeLinecap="round"
    />
  </>
);

const Pookalam = ({ a }) => (
  <>
    <circle
      cx="200"
      cy="150"
      r="140"
      fill="none"
      stroke={a}
      strokeWidth="2"
      strokeDasharray="4 8"
    />
    <circle cx="200" cy="150" r="124" fill="#E36414" />
    <circle cx="200" cy="150" r="100" fill={a} />
    <circle cx="200" cy="150" r="78" fill="#F4E6D6" />
    <circle
      cx="200"
      cy="150"
      r="78"
      fill="none"
      stroke="#C8102E"
      strokeWidth="10"
      strokeDasharray="14 10"
    />
    <circle cx="200" cy="150" r="52" fill="#9B1D20" />
    <circle cx="200" cy="150" r="30" fill="#3E7D4A" />
    <circle cx="200" cy="150" r="12" fill={a} />
  </>
);

const MOTIFS = {
  diyas: Diyas,
  bow: Bow,
  moon: Moon,
  thali: Thali,
  stars: Stars,
  kites: Kites,
  rain: Rain,
  rakhi: Rakhi,
  pookalam: Pookalam,
};

export default function FestivalArt({
  motif,
  accent,
  accent2,
  className = "",
}) {
  // A festival added in the database can name a motif nobody has drawn. The lamp is festival-
  // neutral, so that page looks intentional rather than empty.
  const Shape = MOTIFS[motif] || Diyas;
  return (
    <svg viewBox="0 0 400 300" className={className} aria-hidden="true">
      <Shape a={accent} b={accent2} />
    </svg>
  );
}
